import { cloneDeep, set } from "lodash";
import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import ApiStateModal from "../../../components/reusable-ui/basic-modal";
import { MESSAGE_TYPE, COMPONENT_MODES } from "../../../data/constants";
import { isAddNewMode, isEditMode } from "../../../helpers/catalog-helper";
import {
  addCharge,
  getAllChargesData,
  updateChargesList,
} from "../../../redux/slices";
import { RootState } from "../../../redux/store";
import ChargesEditView from "./charges-edit";
import ChargesViewComponent from "./charges-view";
import "./style.scss";

const ChargesManagement: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const INITIAL_CHARGE_DETAILS = {
    chargeType: "Percentage",
    applyAt: "Item",
    isIncludesTax: false,
    isDirectCharge: false,
    channelInfoList: [],
  };
  const [chargeId, setChargeId] = useState<string>("");
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMessageMap, setErrorMsgMap] = useState<any>({});
  const [chargeDetails, setChargeDetails] = useState<any>(
    INITIAL_CHARGE_DETAILS
  );
  const [modeConfig, setModeConfig] = useState<any>({
    //it will determine whether it's in view mode or edit mode
    type: COMPONENT_MODES.viewMode,
    tabId: "",
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalText, setSuccessModalText] = useState("");
  const [chargeNamesList, setChargeNamesList] = useState<any>([]);

  const dispatch = useDispatch();
  const chargesDataMap = useSelector(
    (state: RootState) => state.chargesAndDiscountsReducer.chargesDataMap
  );

  useEffect(() => {
    if (chargesDataMap && Object.keys(chargesDataMap)?.length) {
      let dataToUpdate: any = chargesDataMap
        ? Object.values(chargesDataMap).map(
            (chargeData: any, index: number) => {
              if (chargeId !== chargeData?.mid) {
                return chargeData?.name;
              }
            }
          )
        : [];
      setChargeNamesList(dataToUpdate);
    }
  }, [chargesDataMap, chargeId]);

  useEffect(() => {
    if (isAddNewMode(modeConfig.type) || !chargeId) {
      setChargeDetails(INITIAL_CHARGE_DETAILS);
      setErrorMsgMap({});
    }
  }, [modeConfig, chargeId]);

  useEffect(() => {
    if (!isAddNewMode(modeConfig?.type) && chargeId) {
      setChargeDetails(chargesDataMap[chargeId]);
    }
  }, [chargeId, chargesDataMap]);

  const updateChargeDataInParentState = (key: string, value: any) => {
    const dataToUpdate = cloneDeep(chargeDetails) || {};
    set(dataToUpdate, key, value);
    setChargeDetails(dataToUpdate);
    setIsChangesMade(true);
  };

  function resetStateOnClose() {
    setErrorMsgMap({});
    setIsChangesMade(false);
    setModeConfig({ type: COMPONENT_MODES.viewMode });
  }

  function clearChargeData() {
    setChargeDetails(INITIAL_CHARGE_DETAILS);
  }
  const onDiscardCtaclick = () => {
    resetStateOnClose();
    if (!isAddNewMode(modeConfig?.type)) {
      if (chargeId) {
        setChargeDetails(cloneDeep(chargesDataMap[chargeId]));
      } else {
        setChargeDetails(INITIAL_CHARGE_DETAILS);
      }
    }
  };

  const validateChargeDetails = (chargeDetails: any) => {
    if (
      chargeDetails?.name &&
      chargeNamesList.indexOf(chargeDetails?.name) === -1 &&
      chargeDetails?.chargeRate
    ) {
      return { isValid: true, dataToPost: chargeDetails };
    }
    let errorMsgMap: any = {};

    if (!chargeDetails?.name) {
      errorMsgMap.name = "This is mandatory field";
    }

    if (
      chargeDetails?.name &&
      chargeNamesList.indexOf(chargeDetails?.name) !== -1
    ) {
      errorMsgMap.name = "Name should be unique";
    }

    if (!chargeDetails?.chargeRate) {
      errorMsgMap.chargeRate = "This is mandatory field";
    }

    return { isValid: false, errorMsgMap };
  };

  const onSaveCtaClick = () => {
    const validatedData: any = validateChargeDetails(chargeDetails);
    const callback = (response: any) => {
      if (isEditMode(modeConfig?.type)) {
        dispatch(updateChargesList({ key: chargeId, data: response }));
        setSuccessModalText("Changes Saved Successfully");
      } else if (isAddNewMode(modeConfig?.type)) {
        dispatch(getAllChargesData());
        setSuccessModalText("Charge Created Successfully");
      }
      setShowSuccessModal(true);
      resetStateOnClose();
    };
    if (validatedData?.isValid) {
      dispatch(
        addCharge({
          dataToPost: chargeDetails,
          callback,
        })
      );
    } else {
      setErrorMsgMap(validatedData?.errorMsgMap);
    }
  };

  const commonProps = {
    data: chargeDetails,
    modeConfig,
    setModeConfig,
    updateChargeDataInParentState,
    chargeId,
    errorMessageMap,
    onSaveCtaClick,
    onDiscardCtaclick,
    isChangesMade,
    clearChargeData,
  };

  return (
    <section className="flex h-[calc(100vh-141px)] overflow-hidden sm:flex-col chargesManageContainer">
      <ChargesViewComponent
        openChargeView={setChargeId}
        {...commonProps}
      ></ChargesViewComponent>

      {chargeId ? <ChargesEditView {...commonProps} /> : null}

      {showSuccessModal && (
        <ApiStateModal
          modalType={MESSAGE_TYPE.success}
          open={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          mainText={successModalText}
        ></ApiStateModal>
      )}
    </section>
  );
};

export default ChargesManagement;
